import { useCallback, useMemo } from 'react';
import { ChangeEvent } from 'react';
import { AbTest } from '../../../models/AbTest';
import {
  AbTestFormData,
  validateDetails,
  validateStatisticalSignificance,
  validateNumberOfCheckouts,
  buildAbTestWidgetsForVariant,
} from './abTestModalUtils';
import { AbTestWidgetType, AbTestWidgetTypeToTypeCategory } from '../../../models/AbTestCreationType';
import { numberToLetter } from '@utils/number-utils';
import { Shop } from '@pdqdev/pdq-shared/dist/entities/Shop';
import { useCheckoutApi } from '@app/react-common/api/useCheckoutApi';
import { AbtestRuleEngineConfig } from '../config/AbTestRuleEngineConfig';
import { getRulesForTestType } from '../config/AbTestRulesByType';
import { AbTestTypeCategory } from '../../../models/AbTestCreationType';
import { RuleEngineConfig } from '@app/react-common/components/rule-engine-v2/models/RuleEngineConfig';
import { getIsThemeTest } from '../../../utils';

interface UseAbTestModalCommonProps {
  mode: 'create' | 'edit' | 'view' | 'duplicate';
  formData: AbTestFormData;
  setFormData: React.Dispatch<React.SetStateAction<AbTestFormData>>;
  rulesValid: boolean;
  setRulesValid: (valid: boolean) => void;
  setStatisticalSignificanceError: (error: string | undefined) => void;
  statisticalSignificanceError: string | undefined;
  widgetType: AbTestWidgetType;
  shop: Shop;
  existingAbTest?: AbTest;
  saveTest?: (formData: AbTest) => Promise<number | undefined>;
  onSaveSuccess: () => void;
  onClose: () => void;
  activeTab: string;
  setActiveTab: (tab: string) => void;
  TAB_ORDER: string[];
  showToast?: (options: { message: string; mode: string }) => void;
  // Optional callback for custom post-save behavior (e.g., navigation to Token tab)
  onAfterSave?: (testId: number | undefined) => void;
  // Optional state setters for test creation tracking
  setTestCreated?: (created: boolean) => void;
  setSavedAbTestId?: (id: number | null) => void;
}

export const useAbTestModalCommon = ({
  mode,
  formData,
  setFormData,
  rulesValid,
  setRulesValid,
  setStatisticalSignificanceError,
  statisticalSignificanceError,
  widgetType,
  shop,
  existingAbTest,
  saveTest,
  onSaveSuccess,
  onClose,
  activeTab,
  setActiveTab,
  TAB_ORDER,
  showToast,
  onAfterSave,
  setTestCreated,
  setSavedAbTestId,
}: UseAbTestModalCommonProps) => {
  const getNextTab = useCallback(
    (currentTabName: string): string | null => {
      const currentIndex = TAB_ORDER.indexOf(currentTabName);
      if (currentIndex >= 0 && currentIndex < TAB_ORDER.length - 1) {
        return TAB_ORDER[currentIndex + 1];
      }
      return null;
    },
    [TAB_ORDER],
  );

  const getPreviousTab = useCallback(
    (currentTabName: string): string | null => {
      const currentIndex = TAB_ORDER.indexOf(currentTabName);
      if (currentIndex > 0) {
        return TAB_ORDER[currentIndex - 1];
      }
      return null;
    },
    [TAB_ORDER],
  );

  const setFormDataValue = useCallback(
    (name: string, value: any) => {
      setFormData((prevData) => ({ ...prevData, [name]: value }));
    },
    [setFormData],
  );

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement> | ChangeEvent<HTMLTextAreaElement>) => {
      const { name, value } = e.target;
      if (name === 'statisticalSignificance') {
        const error = validateStatisticalSignificance(value);
        setStatisticalSignificanceError(error);
        if (error) return;
      }

      if (name === 'numberOfCheckouts') {
        if (!validateNumberOfCheckouts(value)) {
          return;
        }
      }
      setFormDataValue(name, value);
    },
    [setFormDataValue, setStatisticalSignificanceError],
  );

  const handleSave = useCallback(async () => {
    if (!saveTest || !shop) return;

    try {
      const testType = AbTestWidgetTypeToTypeCategory[widgetType];

      const testId = await saveTest({
        ...existingAbTest,
        name: formData.name,
        description: formData.description,
        numberOfCheckouts: formData.numberOfCheckouts,
        optimizingFor: formData.optimizingFor,
        statisticalSignificance: formData.statisticalSignificance,
        endedAt: existingAbTest ? existingAbTest.endedAt : undefined,
        startedAt: existingAbTest ? existingAbTest.startedAt : undefined,
        shopId: shop.id,
        testType: testType,
        abTestRules: formData.abTestRules,
        abTestVariants: formData.abTestVariants
          .filter((x) => x.isSelected)
          .sort((a, b) => (a.slotIndex ?? 0) - (b.slotIndex ?? 0))
          .map((variant, idx) => ({
            id: variant.apiId ? variant.apiId : undefined,
            name: numberToLetter(idx + 1),
            percentageTraffic: variant.percentageTraffic,
            description: null,
            abTestWidgets: buildAbTestWidgetsForVariant(
              variant,
              widgetType,
              getIsThemeTest(testType) ? { metadata: { checkoutGroupIds: variant.checkoutGroupIds || [] } } : {},
            ),
          })),
      });

      // Update test creation tracking state if provided
      if (setTestCreated) setTestCreated(true);
      if (setSavedAbTestId) setSavedAbTestId(testId || null);

      // If custom post-save handler provided, use it; otherwise default to onSaveSuccess
      if (onAfterSave) {
        onAfterSave(testId);
      } else {
        onSaveSuccess();
      }
    } catch (error) {
      onClose();
    }
  }, [
    saveTest,
    shop,
    widgetType,
    existingAbTest,
    formData,
    onSaveSuccess,
    onClose,
    onAfterSave,
    setTestCreated,
    setSavedAbTestId,
  ]);

  const handleDetailsValidation = useCallback(
    (nextTab: string, setShowInvalidStep?: (step: number) => void) => {
      if (validateDetails(formData)) {
        if (setShowInvalidStep) setShowInvalidStep(0);
        setActiveTab(nextTab);
      } else {
        if (setShowInvalidStep) setShowInvalidStep(1);
      }
    },
    [formData, setActiveTab],
  );

  const handleRulesValidation = useCallback(
    (nextTab: string, setShowInvalidStep?: (step: number) => void) => {
      if (!rulesValid) {
        if (setShowInvalidStep) setShowInvalidStep(2);
      } else {
        if (setShowInvalidStep) setShowInvalidStep(0);
        setActiveTab(nextTab);
      }
    },
    [rulesValid, setActiveTab],
  );

  const handleTabChange = useCallback(
    (tab: string, setShowInvalidStep?: (step: number) => void) => {
      if (mode === 'view') {
        setActiveTab(tab);
        return;
      }
      if (activeTab === 'Details') {
        handleDetailsValidation(tab, setShowInvalidStep);
      } else if (activeTab === 'Rules') {
        handleRulesValidation(tab, setShowInvalidStep);
      } else {
        setActiveTab(tab);
      }
    },
    [mode, activeTab, setActiveTab, handleDetailsValidation, handleRulesValidation],
  );

  const failureHandler = useCallback(async () => {
    if (showToast) {
      showToast({ message: `An error has occurred. Please contact support if the problem persists.`, mode: 'danger' });
    }
  }, [showToast]);

  const checkoutApi = useCheckoutApi(failureHandler);

  const filteredRulesBasedOnTestTypeCatagory: RuleEngineConfig = useMemo(
    () => ({
      ...AbtestRuleEngineConfig,
      properties: AbtestRuleEngineConfig.properties.filter((configProperty) => {
        const testTypeCategoryForRules =
          widgetType === AbTestWidgetType.CHECKOUT ? AbTestTypeCategory.SHIPPING_RATES : AbTestTypeCategory.EXTENSIONS;
        const allowedRules = getRulesForTestType(testTypeCategoryForRules);
        return allowedRules.includes(configProperty.property);
      }),
    }),
    [widgetType],
  );

  return {
    getNextTab,
    getPreviousTab,
    handleInputChange,
    setFormDataValue,
    handleSave,
    handleTabChange,
    handleDetailsValidation,
    handleRulesValidation,
    filteredRulesBasedOnTestTypeCatagory,
    checkoutApi,
  };
};
